import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export type RetentionKey =
  | 'inactiveAccounts'
  | 'unverifiedAccounts'
  | 'contactMessages'
  | 'chatMessages'
  | 'rejectedSubmissions'
  | 'staffActivities'
  | 'newsletterProofs';

export interface PrivacyProcessor {
  name: string;
  // processor : sous-traitant agissant pour PromoDeep ; recipient : responsable de son propre traitement
  role: 'processor' | 'recipient';
  purpose: string;
  data: string;
  location: string;
  safeguards: string;
  url: string;
}

export interface PrivacyInfo {
  controller: { name: string; address: string; email: string; dpoEmail: string };
  retention: Record<RetentionKey, number>;
  inactiveWarningDays: number;
  processors: PrivacyProcessor[];
  updatedAt: string | null;
}

// Unité de chaque durée (mêmes règles que RETENTION_RULES dans l'API, src/privacy/privacy.rules.ts)
export const RETENTION_UNITS: Record<RetentionKey, 'days' | 'months'> = {
  inactiveAccounts: 'months',
  unverifiedAccounts: 'days',
  contactMessages: 'months',
  chatMessages: 'months',
  rejectedSubmissions: 'months',
  staffActivities: 'months',
  newsletterProofs: 'months',
};

// Copie des valeurs par défaut de l'API, affichée si elle ne répond pas (rendu SSR, API arrêtée)
const FALLBACK: PrivacyInfo = {
  controller: {
    name: 'TechnoDeep France (micro-entreprise), éditeur de PromoDeep',
    address: '28 Rue Danielle Casanova B 005, 95100 Argenteuil, France',
    email: '',
    dpoEmail: '',
  },
  retention: {
    inactiveAccounts: 36, unverifiedAccounts: 30, contactMessages: 24, chatMessages: 24,
    rejectedSubmissions: 12, staffActivities: 24, newsletterProofs: 36,
  },
  inactiveWarningDays: 30,
  processors: [
    { name: 'OVH SAS', role: 'processor', purpose: 'Hébergement du site, de l\'API et de la base de données', data: 'Toutes les données traitées par le site', location: 'France (Union européenne)', safeguards: '', url: 'https://www.ovhcloud.com/fr/personal-data-protection/' },
    { name: 'Brevo (Sendinblue SAS)', role: 'processor', purpose: 'Envoi de la newsletter et des offres des partenaires', data: 'Adresse e-mail, langue, liste choisie', location: 'France (Union européenne)', safeguards: '', url: 'https://www.brevo.com/fr/legal/privacypolicy/' },
    { name: 'Google Ireland Ltd (Gmail)', role: 'processor', purpose: 'Envoi des e-mails liés au compte (validation, mot de passe, avertissements)', data: 'Adresse e-mail, contenu du message', location: 'Union européenne et États-Unis', safeguards: 'EU-US Data Privacy Framework, clauses contractuelles types', url: 'https://policies.google.com/privacy' },
    { name: 'Google Ireland Ltd (Google Analytics)', role: 'processor', purpose: 'Mesure d\'audience, seulement si vous l\'acceptez dans la bannière cookies', data: 'Identifiant de cookie, pages vues, appareil, adresse IP', location: 'Union européenne et États-Unis', safeguards: 'EU-US Data Privacy Framework, clauses contractuelles types', url: 'https://policies.google.com/privacy' },
    { name: 'Google Ireland Ltd (Google Ads, AdSense)', role: 'recipient', purpose: 'Publicité et mesure des conversions, seulement si vous l\'acceptez dans la bannière cookies', data: 'Identifiants de cookies publicitaires, pages vues, adresse IP', location: 'Union européenne et États-Unis', safeguards: 'EU-US Data Privacy Framework, clauses contractuelles types', url: 'https://policies.google.com/technologies/ads' },
    { name: 'Google (serveurs STUN)', role: 'processor', purpose: 'Mise en relation des appels audio et vidéo entre membres', data: 'Adresse IP pendant l\'appel', location: 'Union européenne et États-Unis', safeguards: 'EU-US Data Privacy Framework', url: 'https://policies.google.com/privacy' },
    { name: 'Services de notification des navigateurs (Google, Mozilla, Apple, Microsoft)', role: 'recipient', purpose: 'Acheminement des notifications push, seulement si vous les activez', data: 'Identifiant d\'abonnement push, contenu de la notification', location: 'Selon votre navigateur', safeguards: '', url: '' },
    { name: 'Awin AG', role: 'recipient', purpose: 'Suivi des clics sur les liens affiliés et calcul des commissions', data: 'Clic sur un lien, identifiant de cookie, achat éventuel chez le marchand', location: 'Allemagne (Union européenne)', safeguards: '', url: 'https://www.awin.com/fr/politique-de-confidentialite' },
    { name: 'Amazon Europe Core SARL (Partenaires Amazon)', role: 'recipient', purpose: 'Suivi des clics sur les liens Amazon et calcul des commissions', data: 'Clic sur un lien, identifiant de cookie, achat éventuel sur Amazon', location: 'Luxembourg (Union européenne)', safeguards: '', url: 'https://www.amazon.fr/gp/help/customer/display.html?nodeId=201909010' },
  ],
  updatedAt: null,
};

// Politique de confidentialité : responsable, durées de conservation et prestataires réglés dans le back office
// (Administration → Privacy (GDPR)), lus une fois par session (route publique GET /privacy/public)
@Injectable({
  providedIn: 'root'
})
export class PrivacyInfoService {

  readonly info$: Observable<PrivacyInfo>;

  constructor(http: HttpClient) {
    this.info$ = http.get<PrivacyInfo>(environment.host + '/privacy/public').pipe(
      map(info => info && info.retention && Array.isArray(info.processors) ? info : FALLBACK),
      catchError(() => of(FALLBACK)),
      shareReplay(1),
    );
  }
}
